import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import CountdownTimer from "../../../features/countdown-timer";
import { useNavigate } from "react-router-dom";
import OtpInputField from "../../../components/input-fields/otp-input-field";
import Tooltip from "../../../components/miscellaneous/tooltip";
import ErrorToast from "../../../components/toasts/error-toast";
import axios from "axios";
import AmpleHireLoader from "../../../components/loaders/ample-hire-loader";
import M from "materialize-css";
import {
  setUserState,
} from "../../../redux/slices/signin-slice";
import Cookies from "js-cookie";
import { useDispatch } from "react-redux";
import {
  getCountryCode,
  getPhoneNumber,
} from "../../../utils/phoneNumberUtils";
import { useLocation } from 'react-router-dom';
import axiosInstance from "../../../interceptors";
import SuccessToast from "../../../components/toasts/success-toast";
import { useGlobalContext } from "../../../context";
import { baseUrl } from "../../../config/config";

const numberOfDigits = 6;
const initialTime = 1 * 60;

/* ── animation variants ── */
const leftPanelVariants = {
  hidden: { x: "-100%", opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

const rightPanelVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: 0.3, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.08, delayChildren: 0.5 }
  }
};

const fieldVariant = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
};

const LoginWithOTP = ({
  resendOtp = false,
  setResendSent = () => { },
  mobileNumber,
  prevNav = "/",
  callBackFn = () => { },
  isModal = false,
  otpSent = true,
  setOtpSent = () => { },
  actualOTP = "",
  otpInitialTime = initialTime,
}) => {
  const [otp, setOtp] = useState(new Array(numberOfDigits).fill(""));
  const otpBoxReference = useRef([]);
  const [otpTime, setOtpTime] = useState(otpInitialTime);
  const [loading, setLoading] = useState(false);
  const [allowContinue, setAllowContinue] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { hostname } = useGlobalContext();
  console.log('hostname enter otp page ::: ', hostname);
  const { ActualOTP } = location?.state ? location.state : "";
  actualOTP = ActualOTP ? ActualOTP : actualOTP

  // Handling OTP input
  const handleSetOtpChange = (value, index) => {
    let newArr = [...otp];
    newArr[index] = value;
    setOtp(() => newArr);

    if (value && index < numberOfDigits - 1) {
      otpBoxReference.current[index + 1].focus();
    }
  };

  // Handle backspace key
  const handleBackspace = (e, index) => {
    if (e.key === "Backspace") {
      if (index > 0) {
        if (!otp[index]) {
          otpBoxReference.current[index - 1].focus();
        }
        let newArr = [...otp];
        newArr[index] = "";
        setOtp(() => newArr);
      }
    }
  };

  const handleEnterOTP = async (e) => {
    e.preventDefault();

    setOtpTime(otpInitialTime);
    setOtpSent(true);

    try {
      setLoading(true);
      const countryCode = getCountryCode(mobileNumber);
      const nationalNumber = getPhoneNumber(mobileNumber);

      const response = await axiosInstance.post(
        `${baseUrl}/common/otp/generate-otp`,
        { phoneCode: `+${countryCode}`, mobileNo: nationalNumber }
      );
      const data = response.data;
      if (
        data.success ||
        response.status === 200 ||
        response.statusText === "OK"
      ) {
        SuccessToast(data.message || "verification code sent successfully!");
         navigate("/signin/enter-otp", { state: { ActualOTP: data.otp } });
      }
    } catch (error) {
      ErrorToast(error.message || "Error sending the OTP!");
    } finally {
      setLoading(false);
    }
  };

  // Handle Submit OTP
  const handleSubmitOtp = async (e) => {
    e.preventDefault();

    let stringOtp = otp.toString();
    stringOtp = stringOtp.split(",").join("");
    const countryCode = getCountryCode(mobileNumber);
    const nationalNumber = getPhoneNumber(mobileNumber);

    if (actualOTP !== stringOtp) {
      ErrorToast("Invalid verification code!");
      return;
    }
    if (isModal) {
      return callBackFn(actualOTP === stringOtp);
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("grant_type", "password");
      formData.append("client_id", "ev-client");
      formData.append("client_secret", "egov");
      formData.append("username", nationalNumber);
      formData.append("password", stringOtp);
      const AUTH_API_URL = process.env.REACT_APP_APP_AUTH_URL;
      const response = await axios.post(
        `${AUTH_API_URL}/oauth2/token`,
        formData
      );
      const data = response.data;
      if (
        data.success ||
        response.status === 200 ||
        response.status === "OK" ||
        response.statusText === "OK"
      ) {
        const user = response.data.firstName;
        const firstTimeSignIn = response.data.firstTimeSignIn;
        const userType = response.data.userType;
        const tncStatus = response.data.tnCstatus;
        const userId = response.data.pk;
        const token = response.data.access_token;

        dispatch(setUserState({
          userName: user,
          userType,
          tncStatus,
          userId,
          token
        }));

        Cookies.set("e_access_token", token, {
          expires: 1,
        });
        // Unified routing: all authenticated users land in the /admin app.
        // (Previously userType "Others" was sent to the legacy /user app.)
        if (userType === "Others") {
          navigate("/admin/dashboard-v2");
          return;
        }
        if (firstTimeSignIn) {
          navigate("/terms-of-use");
        } else {
          navigate("/admin/candidate-video-answers");
        }
      }
    } catch (error) {
      ErrorToast(error.message || "Invalid verification code!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!mobileNumber) {
      if (prevNav) navigate(prevNav);
      else navigate("/signin");
    }
  }, [mobileNumber, navigate]);

  useEffect(() => {
    if (!isModal) {
      M.AutoInit();
    }
    return () => {
      setOtp(new Array(numberOfDigits).fill(""));
    };
  }, [isModal]);

  useEffect(() => {
    setAllowContinue(otp.every((s) => s !== ""));
  }, [otp]);

  /* ── inline styles ── */
  const styles = {
    wrapper: {
      display: "flex",
      minHeight: "100vh",
      fontFamily: "'DM Sans', system-ui, sans-serif",
      background: "#F8F6F3",
      overflow: "hidden",
    },
    leftPanel: {
      flex: "0 0 44%",
      background: "linear-gradient(160deg, #1B2332 0%, #243044 50%, #1B2332 100%)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      padding: "60px 48px",
      position: "relative",
      overflow: "hidden",
      color: "#FFFFFF",
    },
    meshOverlay: {
      position: "absolute",
      inset: 0,
      opacity: 0.06,
      backgroundImage:
        `radial-gradient(circle at 20% 30%, #E8753A 0%, transparent 50%),
         radial-gradient(circle at 80% 70%, #2D9C83 0%, transparent 50%),
         radial-gradient(circle at 50% 10%, #E8753A 0%, transparent 40%)`,
      pointerEvents: "none",
    },
    gridPattern: {
      position: "absolute",
      inset: 0,
      opacity: 0.04,
      backgroundImage:
        `linear-gradient(rgba(255,255,255,.15) 1px, transparent 1px),
         linear-gradient(90deg, rgba(255,255,255,.15) 1px, transparent 1px)`,
      backgroundSize: "48px 48px",
      pointerEvents: "none",
    },
    cornerGlow: {
      position: "absolute",
      bottom: "-80px",
      left: "-80px",
      width: "280px",
      height: "280px",
      borderRadius: "50%",
      background: "radial-gradient(circle, rgba(232,117,58,0.12) 0%, transparent 70%)",
      pointerEvents: "none",
    },
    brandBlock: {
      position: "relative",
      zIndex: 1,
      textAlign: "center",
      maxWidth: "380px",
    },
    logoMark: {
      width: "56px",
      height: "56px",
      borderRadius: "14px",
      background: "linear-gradient(135deg, #E8753A 0%, #D4622B 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 28px",
      fontSize: "22px",
      fontWeight: 800,
      fontFamily: "'Outfit', system-ui, sans-serif",
      color: "#fff",
      boxShadow: "0 8px 24px rgba(232,117,58,0.25)",
    },
    brandName: {
      fontFamily: "'Outfit', system-ui, sans-serif",
      fontSize: "28px",
      fontWeight: 700,
      marginBottom: "8px",
      letterSpacing: "-0.5px",
    },
    tagline: {
      fontFamily: "'Outfit', system-ui, sans-serif",
      fontSize: "36px",
      fontWeight: 700,
      lineHeight: 1.15,
      marginBottom: "16px",
      letterSpacing: "-0.8px",
    },
    subtitle: {
      fontSize: "15px",
      lineHeight: 1.6,
      color: "rgba(255,255,255,0.6)",
      maxWidth: "320px",
      margin: "0 auto",
    },
    accentBar: {
      width: "48px",
      height: "3px",
      borderRadius: "2px",
      background: "#E8753A",
      margin: "28px auto 0",
    },
    rightPanel: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "48px 40px",
      background: "#F8F6F3",
      overflowY: "auto",
    },
    formContainer: {
      width: "100%",
      maxWidth: "440px",
    },
    formHeading: {
      fontFamily: "'Outfit', system-ui, sans-serif",
      fontSize: "26px",
      fontWeight: 700,
      color: "#1B2332",
      marginBottom: "4px",
      letterSpacing: "-0.4px",
    },
    formSubhead: {
      fontSize: "14px",
      color: "#6B7280",
      marginBottom: "32px",
    },
    otpRow: {
      display: "flex",
      gap: "10px",
      justifyContent: "center",
      marginBottom: "24px",
    },
    resendRow: {
      textAlign: "center",
      fontSize: "13px",
      color: "#6B7280",
      marginBottom: "28px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      flexWrap: "wrap",
    },
    resendLink: {
      color: "#E8753A",
      fontWeight: 600,
      cursor: "pointer",
      border: "none",
      background: "none",
      padding: 0,
      fontSize: "13px",
    },
    primaryBtn: {
      width: "100%",
      padding: "14px 0",
      background: "linear-gradient(135deg, #E8753A 0%, #D4622B 100%)",
      color: "#FFFFFF",
      border: "none",
      borderRadius: "10px",
      fontSize: "15px",
      fontWeight: 700,
      fontFamily: "'DM Sans', sans-serif",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 4px 14px rgba(232,117,58,0.3)",
    },
    disabledBtn: {
      width: "100%",
      padding: "14px 0",
      background: "#D1D5DB",
      color: "#9CA3AF",
      border: "none",
      borderRadius: "10px",
      fontSize: "15px",
      fontWeight: 700,
      fontFamily: "'DM Sans', sans-serif",
      cursor: "not-allowed",
    },
  };

  const responsiveCss = `
    @media (max-width: 900px) {
      .ample-otp-wrapper { flex-direction: column !important; }
      .ample-otp-left {
        flex: none !important;
        min-height: 200px !important;
        padding: 36px 24px !important;
      }
      .ample-otp-right { padding: 32px 20px !important; }
    }
  `;

  /* ── If used inside a modal, render compact version ── */
  if (isModal) {
    return (
      <>
        {loading && <AmpleHireLoader />}
        <div
          className={`col xl6 l6 m6 s12 login-wrap isModal`}
        >
          <div className="login-wrap-box">
            <h3>Verify your mobile number</h3>
            <p>We have just sent a 6 digit verification code to your phone number</p>
            <div
              className="loginform otp-form"
              onSubmit={handleSubmitOtp}
              style={{ height: "200px", padding: "32px" }}
            >
              <div className="otp-field">
                {otp.map((otpVal, index) => (
                  <OtpInputField
                    key={index}
                    index={index}
                    otpBoxReference={otpBoxReference}
                    value={otpVal}
                    onChange={(e) => handleSetOtpChange(e.target.value, index)}
                    ref={(reference) =>
                      (otpBoxReference.current[index] = reference)
                    }
                    onKeyDown={(e) => handleBackspace(e, index)}
                  />
                ))}
              </div>
              <span className="otp-recived">
                <div style={{display: 'inline-block'}}>
                  <i className="show-details infermation-ico-black"
                    style={{ padding: '0', marginRight: '5px', marginBottom: '5px'}}>
                    i
                    <Tooltip divTagCssClasses={"infbox-click"}>
                      <p>
                        When the verification code timer reaches 0, click on 'Resend verification code' to receive a new verification code and verify your number.
                      </p>
                    </Tooltip>
                  </i>
                </div>
                Didn't receive verification code?
                <font style={{marginLeft: '10px'}}>
                  {otpSent && (
                    <CountdownTimer
                      otpTime={otpTime}
                      setOtpTime={setOtpTime}
                      prevNav={prevNav}
                      otpSent={otpSent}
                      setOtpSent={setOtpSent}
                      originalOtpTime={initialTime}
                      startTimer={otpSent}
                    />
                  )}
                  {otpTime === 0 ? (
                    <span
                      style={{ color: 'red', cursor: 'pointer' }}
                      onClick={handleEnterOTP}
                    >
                      Resend verification code
                    </span>
                  ) : null}
                </font>
              </span>
              <button
                className={`waves-effect waves-light btn btn-large fullwidth-btn graybtn modal-close ${allowContinue ? "" : "disabled"}`}
                onClick={handleSubmitOtp}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ── Full-page two-panel OTP layout ── */
  return (
    <>
      {loading && <AmpleHireLoader />}
      <style>{responsiveCss}</style>

      <div className="ample-otp-wrapper" style={styles.wrapper}>

        {/* ═══════ LEFT BRAND PANEL ═══════ */}
        <motion.div
          className="ample-otp-left"
          style={styles.leftPanel}
          variants={leftPanelVariants}
          initial="hidden"
          animate="visible"
        >
          <div style={styles.meshOverlay} />
          <div style={styles.gridPattern} />
          <div style={styles.cornerGlow} />

          <div style={styles.brandBlock}>
            <div style={styles.logoMark}>A</div>
            <div style={styles.brandName}>Ample Hire</div>
            <div style={styles.tagline}>Verify Identity</div>
            <p style={styles.subtitle}>
              One last step to keep your account secure. Enter the verification
              code we sent to your device.
            </p>
            <div style={styles.accentBar} />
          </div>
        </motion.div>

        {/* ═══════ RIGHT OTP PANEL ═══════ */}
        <motion.div
          className="ample-otp-right"
          style={styles.rightPanel}
          variants={rightPanelVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            style={styles.formContainer}
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={fieldVariant}>
              <h2 style={styles.formHeading}>Verify your mobile number</h2>
              <p style={styles.formSubhead}>
                We have just sent a 6 digit verification code to your phone number
              </p>
            </motion.div>

            <motion.div variants={fieldVariant} style={styles.otpRow}>
              {otp.map((otpVal, index) => (
                <OtpInputField
                  key={index}
                  index={index}
                  otpBoxReference={otpBoxReference}
                  value={otpVal}
                  onChange={(e) => handleSetOtpChange(e.target.value, index)}
                  ref={(reference) =>
                    (otpBoxReference.current[index] = reference)
                  }
                  onKeyDown={(e) => handleBackspace(e, index)}
                />
              ))}
            </motion.div>

            <motion.div variants={fieldVariant} style={styles.resendRow}>
              <div style={{display: 'inline-block'}}>
                <i className="show-details infermation-ico-black"
                  style={{ padding: '0', marginRight: '5px', marginBottom: '5px'}}>
                  i
                  <Tooltip divTagCssClasses={"infbox-click"}>
                    <p>
                      When the verification code timer reaches 0, click on 'Resend verification code' to receive a new verification code and verify your number.
                    </p>
                  </Tooltip>
                </i>
              </div>
              Didn't receive verification code?
              <span style={{marginLeft: '6px'}}>
                {otpSent && (
                  <CountdownTimer
                    otpTime={otpTime}
                    setOtpTime={setOtpTime}
                    prevNav={prevNav}
                    otpSent={otpSent}
                    setOtpSent={setOtpSent}
                    originalOtpTime={initialTime}
                    startTimer={otpSent}
                  />
                )}
                {otpTime === 0 ? (
                  <span
                    style={styles.resendLink}
                    onClick={handleEnterOTP}
                  >
                    Resend verification code
                  </span>
                ) : null}
              </span>
            </motion.div>

            <motion.div variants={fieldVariant}>
              <button
                style={allowContinue ? styles.primaryBtn : styles.disabledBtn}
                onClick={handleSubmitOtp}
                disabled={!allowContinue}
                onMouseEnter={(e) => {
                  if (allowContinue) {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 6px 20px rgba(232,117,58,0.4)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (allowContinue) {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 14px rgba(232,117,58,0.3)";
                  }
                }}
              >
                Continue
              </button>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </>
  );
};

export default LoginWithOTP;
